import YearChooser from "../../factures/components/YearChooser.tsx";
import { formaters, integerCentToFrenchFinanString } from "vincent-utils";
import BanqueChooser from "./BanqueChooser.tsx";
import HonorairesList from "./HonorairesList.tsx";
import VentilationsContainer from "../containers/VentilationsContainer.tsx";
import React, { useEffect } from "react";
import _ from "underts";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import { BanqueLine2, ManualComptaLine } from "compta-shared";
import { DateTime } from "luxon";
import {
  candidatesFlagger,
  montantCombinaisons2WithMeta,
} from "../processing/montantCombinaisons.ts";
import { RapprochementPanelComps } from "./RapprochementMainComp.tsx";

type PanelType =
  | "rapprocheVirements"
  | "rapprocheCartes"
  | "rapprocheCheques"
  | "olderHonoraires";

interface RapprocheCompProps {
  panel: PanelType;
  yearSelected: number;
  onSetYear: (year: number) => void;
}

const COMBINAISON_SEARCH_LIMITS = {
  maxResults: 400,
  maxVisitedNodes: 180_000,
  maxDurationMs: 120,
} as const;

const getRecetteWindowBoundsFromDate = (date: Date, panel: PanelType) => {
  const baseDate = DateTime.fromJSDate(date);

  switch (panel) {
    case "rapprocheVirements":
      return {
        start: baseDate.minus({ months: 1 }).toJSDate(),
        end: baseDate.plus({ weeks: 1 }).toJSDate(),
      };
    case "rapprocheCheques":
      return {
        start: baseDate.minus({ months: 6 }).toJSDate(),
        end: baseDate.plus({ weeks: 1 }).toJSDate(),
      };
    case "rapprocheCartes":
    case "olderHonoraires":
      return {
        start: baseDate.minus({ days: 5 }).toJSDate(),
        end: baseDate.plus({ days: 5 }).toJSDate(),
      };
    default:
      throw new Error("unknown panel");
  }
};

const getMontant = (banqueLine: BanqueLine2, mode: RapprochementPanelComps) => {
  switch (mode) {
    case "rapprocheVirements":
    case "rapprocheCheques":
      return banqueLine.montantCent;
    case "rapprocheCartes":
      return banqueLine.type === "remiseCartes"
        ? banqueLine.remiseCBBrut
        : null;
    default:
      throw new Error("mode is not defined");
  }
};
const RapprocheComp = (props: RapprocheCompProps) => {
  const handleSelectYear = (year: number) => {
    props.onSetYear(year);
  };

  const [linesChecked, setLinesChecked] = React.useState<string[]>([]);

  const handleToggleCheck = (id: string) => {
    setLinesChecked((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id],
    );
  };
  const currentYear = new Date().getFullYear();
  const previousYears = _.range(5, -1, -1).map((i) => currentYear - i);

  const { data: virementsCréditeurs } =
    trpc.banque.getVirementsCréditeurs.useQuery(props.yearSelected, {
      enabled: props.panel === "rapprocheVirements",
    });

  const { data: remisesChèques } = trpc.banque.getChèquesCréditeurs.useQuery(
    props.yearSelected,
    {
      enabled: props.panel === "rapprocheCheques",
    },
  );

  const { data: remisesCartes } = trpc.banque.getCartesCréditrices.useQuery(
    props.yearSelected,
    {
      enabled: props.panel === "rapprocheCartes",
    },
  );

  const [selectedBanqueLineId, setSelectedBanqueLineId] = React.useState<
    string | null
  >(null);

  const handleSelectBanqueLineId = (id: string) => {
    setSelectedBanqueLineId(id);
    setCombinaisonNumberSelected(null);
    setLinesChecked([]);
  };

  const banqueLines =
    (props.panel === "rapprocheVirements"
      ? virementsCréditeurs
      : props.panel === "rapprocheCheques"
        ? remisesChèques
        : remisesCartes) || [];
  const selectedBanqueLine =
    banqueLines.find((b: BanqueLine2) => b._id === selectedBanqueLineId) ||
    null;
  const [start, setStart] = React.useState<Date>(
    () => getRecetteWindowBoundsFromDate(new Date(), props.panel).start,
  );
  const [end, setEnd] = React.useState<Date>(
    () => getRecetteWindowBoundsFromDate(new Date(), props.panel).end,
  );

  const startInputValue = DateTime.fromJSDate(start).toFormat("yyyy-LL-dd");
  const endInputValue = DateTime.fromJSDate(end).toFormat("yyyy-LL-dd");

  const shiftSearchWindowByMonths = (months: number) => {
    setStart((date) => DateTime.fromJSDate(date).plus({ months }).toJSDate());
    setEnd((date) => DateTime.fromJSDate(date).plus({ months }).toJSDate());
  };

  const onChangeStartDate = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (!parsed.isValid) {
      return;
    }

    const nextStart = parsed.toJSDate();
    setStart(nextStart);
    setEnd((currentEnd) =>
      nextStart.getTime() > currentEnd.getTime() ? nextStart : currentEnd,
    );
  };

  const onChangeEndDate = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (!parsed.isValid) {
      return;
    }

    const nextEnd = parsed.toJSDate();
    setEnd(nextEnd);
    setStart((currentStart) =>
      nextEnd.getTime() < currentStart.getTime() ? nextEnd : currentStart,
    );
  };

  const onPostponeRecetteBookDate = () => {
    shiftSearchWindowByMonths(1);
  };

  const onPreponeRecetteBookDate = () => {
    shiftSearchWindowByMonths(-1);
  };

  useEffect(() => {
    const baseDate = selectedBanqueLine ? selectedBanqueLine.date : new Date();
    const { start: nextStart, end: nextEnd } = getRecetteWindowBoundsFromDate(
      baseDate,
      props.panel,
    );
    setStart(nextStart);
    setEnd(nextEnd);
  }, [props.panel, selectedBanqueLine?._id]);

  const codedLogosMode =
    props.panel === "rapprocheVirements"
      ? "V1"
      : props.panel === "rapprocheCheques"
        ? "B1"
        : "C1";

  const codedMode: ManualComptaLine["mode"] =
    props.panel === "rapprocheVirements"
      ? "Virement1"
      : props.panel === "rapprocheCheques"
        ? "Chèque1"
        : "Carte1";

  const { data: honoraires } =
    trpc.logLine.getHonorairesLinesFromWindow.useQuery(
      {
        start: start,
        end: end,
        mode: codedLogosMode,
      },
      {
        enabled: !!selectedBanqueLine,
      },
    );

  const { data: manualComptaLines } =
    trpc.manualComptaLine.getByDateAndMode.useQuery(
      {
        start: start,
        end: end,
        mode: codedMode,
        isRapproched: false,
      },
      {
        enabled: !!selectedBanqueLine,
      },
    );

  const linesToCompare = React.useMemo(
    () => [
      ...((honoraires &&
        honoraires.map((h) => ({ ...h, type: "LOG_TRESO" as const }))) ||
        []),
      ...((manualComptaLines &&
        manualComptaLines.map((c) => ({
          ...c,
          type: "MANUAL_COMPTA_LINE" as const,
        }))) ||
        []),
    ],
    [honoraires, manualComptaLines],
  );
  const unlockedLinesToCompare = React.useMemo(
    () => linesToCompare.filter((line) => line.locked !== true),
    [linesToCompare],
  );
  const target = selectedBanqueLine
    ? getMontant(selectedBanqueLine, props.panel) || 0
    : 0;

  const chèquesCount = selectedBanqueLine
    ? "remiseChequeCount" in selectedBanqueLine
      ? selectedBanqueLine.remiseChequeCount
      : 0
    : 0;
  const targetElementsCount = chèquesCount > 0 ? chèquesCount : null;

  const { combinaisons, hasReachedLimit } = React.useMemo(() => {
    if (!selectedBanqueLine) {
      return { combinaisons: [], hasReachedLimit: false };
    }
    if (selectedBanqueLine.locked === true) {
      return { combinaisons: [], hasReachedLimit: false };
    }

    if (chèquesCount + linesChecked.length >= 10) {
      return { combinaisons: [], hasReachedLimit: false };
    }

    return montantCombinaisons2WithMeta(
      unlockedLinesToCompare,
      target,
      linesChecked,
      chèquesCount || undefined,
      {
        ...COMBINAISON_SEARCH_LIMITS,
        prioritizeSameDayAs: selectedBanqueLine.date,
      },
    );
  }, [
    selectedBanqueLine,
    target,
    chèquesCount,
    linesChecked,
    unlockedLinesToCompare,
  ]);

  const [combinaisonNumberSelected, setCombinaisonNumberSelected] =
    React.useState<number | null>(null);

  const onSetCombinaisonNumberSelected = (n: number | null) => {
    setCombinaisonNumberSelected(n);
    if (typeof n === "number" && combinaisons[n]) {
      setLinesChecked(combinaisons[n].map((c) => c._id).filter((id) => !!id));
    }
  };

  useEffect(() => {
    if (
      combinaisonNumberSelected != null &&
      !combinaisons[combinaisonNumberSelected]
    ) {
      setCombinaisonNumberSelected(null);
    }
  }, [combinaisonNumberSelected, combinaisons]);

  const combinaisonSelected =
    combinaisonNumberSelected != null
      ? combinaisons[combinaisonNumberSelected]
      : null;
  const combinaisonsFlagged = React.useMemo(
    () => candidatesFlagger(linesToCompare || [], combinaisons),
    [linesToCompare, combinaisons],
  );
  const [showVentilations, setShowVentilations] = React.useState(false);
  const onShowVentilations = (banqueLineId: string) => {
    if (banqueLines.find((line) => line._id === banqueLineId)?.locked === true) {
      return;
    }
    handleSelectBanqueLineId(banqueLineId);
    setShowVentilations(true);
  };
  const onCancelVentilations = () => {
    setShowVentilations(false);
  };

  const utils = trpc.useContext();
  const commitRapprochementMutation =
    trpc.rapprochement.commitMultipleSourceRapprochement.useMutation({
      onSuccess: () => {
        setSelectedBanqueLineId(null);
        setCombinaisonNumberSelected(null);
        setLinesChecked([]);
        utils.banque.getChèquesCréditeurs.refetch();
        utils.banque.getVirementsCréditeurs.refetch();
        utils.banque.getCartesCréditrices.refetch();
        // utils.banque.invalidate();
        //todo: ne fonctionne pas, voir si pas un bug trpc
        utils.logLine.invalidate();
        utils.manualComptaLine.invalidate();
      },
    });

  const actualLinesChecked = React.useMemo(
    () => unlockedLinesToCompare.filter((l) => linesChecked.includes(l._id)),
    [unlockedLinesToCompare, linesChecked],
  );
  const selectedTotalCent = React.useMemo(
    () => actualLinesChecked.reduce((acc, l) => acc + l.montantCent, 0),
    [actualLinesChecked],
  );
  const winningCombinaison = React.useMemo(
    () =>
      combinaisonSelected ||
      (selectedTotalCent === target ? actualLinesChecked : null) ||
      [],
    [combinaisonSelected, actualLinesChecked, selectedTotalCent, target],
  );
  const commitRapprochement = async () => {
    if (
      winningCombinaison.length === 0 ||
      !selectedBanqueLineId ||
      selectedBanqueLine?.locked === true ||
      commitRapprochementMutation.isPending
    )
      return;
    commitRapprochementMutation.mutate({
      banqueLineId: selectedBanqueLineId,
      logLinesIds: winningCombinaison
        .filter((c) => "type" in c && c.type === "LOG_TRESO")
        .map((c) => c._id),
      manualComptaLinesIds: winningCombinaison
        .filter((c) => "type" in c && c.type === "MANUAL_COMPTA_LINE")
        .map((c) => c._id),
    });
  };
  return (
    <div>
      <div>
        <div className="mt-2">
          <div className="row mt-2">
            <div className="col d-flex justify-content-between">
              <YearChooser
                yearChoices={previousYears}
                onYearSelected={handleSelectYear}
                yearSelected={props.yearSelected}
              />
              {winningCombinaison.length > 0 && (
                <button
                  type="button"
                  className={"btn btn-primary"}
                  disabled={
                    commitRapprochementMutation.isPending ||
                    selectedBanqueLine?.locked === true
                  }
                  onClick={commitRapprochement}
                >
                  {commitRapprochementMutation.isPending
                    ? "Enregistrement..."
                    : "Enregistrer rapprochement"}
                </button>
              )}
            </div>
            <div className={"col"}>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onPreponeRecetteBookDate}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-dash-lg"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2 8a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11A.5.5 0 0 1 2 8Z"
                    />
                  </svg>
                </button>
                <div className="btn btn-light d-flex align-items-center gap-2">
                  <span className="small text-muted">du</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startInputValue}
                    onChange={(e) => onChangeStartDate(e.target.value)}
                    style={{ width: "145px" }}
                  />
                  <span className="small text-muted">au</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endInputValue}
                    onChange={(e) => onChangeEndDate(e.target.value)}
                    style={{ width: "145px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onPostponeRecetteBookDate}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-plus-lg"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {commitRapprochementMutation.error ? (
            <div className="alert alert-danger mt-2" role="alert">
              Le rapprochement a échoué :{" "}
              {commitRapprochementMutation.error.message}
            </div>
          ) : null}
          <div className="row mt-2">
            <div className="col">
              <div className="card">
                <div className="card-header">
                  Lignes du compte bancaire non rapprochées
                </div>
                <BanqueChooser
                  banqueLines={banqueLines}
                  selectBanqueLine={handleSelectBanqueLineId}
                  banqueLineSelected={selectedBanqueLine}
                  showVentilations={onShowVentilations}
                />
              </div>
            </div>
            <div className="col">
              <div className="card">
                <div className="card-header">
                  Lignes non rapprochées dans le livre de recettes : du{" "}
                  {formaters.formatDateAsDDN(start)} au{" "}
                  {formaters.formatDateAsDDN(end)}
                </div>
                {selectedBanqueLine && (
                  <div className="px-3 py-2 border-bottom bg-light">
                    <div
                      className="d-grid gap-1 align-items-center"
                      style={{
                        gridTemplateColumns:
                          "minmax(120px, max-content) max-content max-content",
                      }}
                    >
                      <span className="small text-muted fw-semibold">
                        Cible
                      </span>
                      <span
                        className="badge text-bg-secondary"
                        style={{ justifySelf: "start" }}
                      >
                        Nombre cible :{" "}
                        {targetElementsCount != null
                          ? targetElementsCount
                          : "Non imposé"}
                      </span>
                      <span
                        className="badge text-bg-secondary"
                        style={{ justifySelf: "start" }}
                      >
                        Montant cible : {integerCentToFrenchFinanString(target)}
                      </span>
                      <span className="small text-muted fw-semibold">
                        Sélection actuelle
                      </span>
                      <span
                        className="badge text-bg-primary"
                        style={{ justifySelf: "start" }}
                      >
                        Éléments sélectionnés : {actualLinesChecked.length}
                      </span>
                      <span
                        className="badge text-bg-primary"
                        style={{ justifySelf: "start" }}
                      >
                        Total sélectionné :{" "}
                        {integerCentToFrenchFinanString(selectedTotalCent)}
                      </span>
                    </div>
                  </div>
                )}
                {hasReachedLimit && (
                  <div className="alert alert-warning py-2 px-3 m-2 mb-0">
                    Trop de combinaisons possibles. Affichage limité aux{" "}
                    {COMBINAISON_SEARCH_LIMITS.maxResults} premières
                    propositions. Cochez quelques lignes pour affiner.
                  </div>
                )}
                <HonorairesList
                  honoraires={combinaisonsFlagged || []}
                  combinaisonSelected={combinaisonNumberSelected}
                  onSelectCombinaison={onSetCombinaisonNumberSelected}
                  isReady={true} //todo
                  hasData={true} //todo
                  linesChecked={linesChecked}
                  onToggleCheck={handleToggleCheck}
                  disabled={selectedBanqueLine?.locked === true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showVentilations && selectedBanqueLineId ? (
        <VentilationsContainer
          banqueLineId={selectedBanqueLineId}
          onCancelVentilations={onCancelVentilations}
          actionId={null}
          similarLinesIds={[]}
        />
      ) : null}
    </div>
  );
};

export default RapprocheComp;
